import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EMPTY, Subject, finalize, switchMap, takeUntil } from 'rxjs';
import { ConfirmationDialogService } from '../../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataFetch } from '../../../models/data-source';
import { Campaign, campaignServiceLines, campaignServicesLabel, campaignStatuses, campaignStatusLabel } from '../../../models/campaign.models';
import { CampaignApiService, campaignErrorMessage } from '../../../services/campaign-api.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  host: { class: 'workspace-page--full' },
})
export class CampaignsComponent implements OnInit, OnDestroy {
  @ViewChild('table') table?: DataTableComponent<Campaign>;
  @ViewChild('list') list?: DataListComponent<Campaign>;
  deletingId: number | null = null;
  error = '';
  notice = '';
  private readonly destroyed$ = new Subject<void>();
  search = '';
  filters = { status: '', serviceLine: '' };
  readonly statuses = campaignStatuses;
  readonly serviceLines = campaignServiceLines;
  readonly statusLabel = campaignStatusLabel;
  readonly servicesLabel = campaignServicesLabel;
  readonly columns: DataTableColumn[] = [
    { id: 'name', label: 'Campaign' },
    { id: 'serviceLines', label: 'Service lines', sortable: false },
    { id: 'status', label: 'Status' },
    { id: 'budget', label: 'Budget', sortable: false },
    { id: 'startsOn', label: 'Start date' },
    { id: 'endsOn', label: 'End date' },
    { id: 'updatedAt', label: 'Updated' },
  ];
  readonly fetch: DataFetch<Campaign> = query => this.api.list(query);
  readonly trackById = (_: number, campaign: Campaign) => campaign.id;
  constructor(private readonly api: CampaignApiService, public readonly auth: AuthService,
    private readonly confirmation: ConfirmationDialogService) {}

  ngOnInit(): void {
    this.auth.tenant.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.error = this.notice = '';
    });
  }
  canDelete(campaign: Campaign): boolean {
    return campaign.status === 'Draft' && this.auth.hasPermission('Lead.Manage');
  }
  deleteCampaign(campaign: Campaign): void {
    if (!this.canDelete(campaign) || this.deletingId !== null) return;
    this.deletingId = campaign.id;
    this.error = this.notice = '';
    this.confirmation.confirm({ title: 'Delete campaign?',
      message: `Permanently delete “${campaign.name}”? Linked Leads and other map objects will be kept.`,
      confirmLabel: 'Delete', cancelLabel: 'Cancel', danger: true,
    }).pipe(
      switchMap(confirmed => confirmed && this.canDelete(campaign) ? this.api.delete(campaign.id) : EMPTY),
      takeUntil(this.auth.tenant.sessionChanged$),
      takeUntil(this.destroyed$),
      finalize(() => { this.deletingId = null; }),
    ).subscribe({
      next: () => { this.notice = 'Campaign deleted.'; this.refresh(); },
      error: error => { this.error = campaignErrorMessage(error, 'Could not delete this campaign. Try again.'); },
    });
  }
  ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); }
  filter(key: 'status' | 'serviceLine', value: string): void {
    this.filters = { ...this.filters, [key]: value };
  }
  refresh(): void { this.table?.refresh(); this.list?.refresh(); }
}
